/**
 * OverrideFormModal — Phase 07.6.
 *
 * Modal for adding (or replacing) an in-house engine override. Submit
 * routes through `engineReviewApi.addOverride` → backend Phase 06
 * `POST /engine-review/{report_id}/{engine_id}/override`.
 *
 * Field constraints (mirrored from backend `OverrideCreateRequest`):
 *   - field_id: non-empty
 *   - override_value: any (string | number | null)
 *   - original_value: any — the pre-override value being replaced
 *   - note: at least 10 characters (audit-chain requirement)
 *
 * The "auto-trigger rerun" checkbox is on by default — flipping it off
 * lands the override but skips downstream cascade invalidation. Most
 * reviewers leave it on. Surfaced as a checkbox rather than buried in
 * a settings drawer so the cascade implication is visible.
 */

import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';

import Modal from '../../ui/Modal';
import { Button } from '../../ui/Button';
import CpaWorkpaperTextarea from '../../privilege/CpaWorkpaperTextarea';
import type { EngineId, OverridePayload } from '../../../types/engineReview';
import { ENGINE_NAMES } from '../../../types/engineReview';

const MIN_NOTE_LENGTH = 10;

export interface OverrideFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: OverridePayload) => void;
  engineId: EngineId;
  isSubmitting?: boolean;
  /** Optional pre-fill — useful when launching the modal from a specific
   * field's "Override this value" affordance on the engine detail card. */
  initialFieldId?: string;
  initialOriginalValue?: string | number | null;
  /** Optional pre-fill for the override (new) value box. Used by the
   * Forensic Assistant deep-link, which carries the AI's `suggested_value`.
   * May be null when the AI declined to suggest a value (human enters it). */
  initialOverrideValue?: string | number | null;
  /** Optional pre-fill for the reviewer note. The AI deep-link seeds this
   * with the proposal rationale; it remains fully editable and must still
   * meet the 10-char attestation minimum. */
  initialNote?: string;
  /** Provenance of this override. `"from_ai_suggestion"` surfaces the
   * attestation banner; defaults to `"manual"`. Carried into the payload. */
  initialOrigin?: 'manual' | 'from_ai_suggestion';
  /** When `initialOrigin === "from_ai_suggestion"`, the AI audit id that
   * prompted this override. Carried into the payload's
   * `prompted_by_ai_audit_id`. */
  initialPromptedByAiAuditId?: string;
  /** The Forensic Assistant's reasoning for the suggestion. Shown read-only in
   * the attestation banner for reference. It is deliberately NOT used to
   * pre-fill the reviewer note: the attestation must be the human's own words,
   * not AI text the CPA rubber-stamps. */
  aiRationale?: string;
}

const trimOrEmpty = (s: string) => s.trim();

const parseValue = (raw: string): string | number | null => {
  // Empty input → null. Number-shaped strings → number. Otherwise → string.
  // Backend accepts Any so the precise type matters mainly for downstream
  // numeric calculations and audit-chain serialization.
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const n = Number(trimmed);
    if (!Number.isNaN(n)) return n;
  }
  return trimmed;
};

const toRawString = (v: string | number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);

export default function OverrideFormModal({
  isOpen,
  onClose,
  onSubmit,
  engineId,
  isSubmitting = false,
  initialFieldId = '',
  initialOriginalValue = null,
  initialOverrideValue = null,
  initialNote = '',
  initialOrigin = 'manual',
  initialPromptedByAiAuditId,
  aiRationale,
}: OverrideFormModalProps) {
  const [fieldId, setFieldId] = useState(initialFieldId);
  const [originalValueRaw, setOriginalValueRaw] = useState(
    toRawString(initialOriginalValue),
  );
  const [overrideValueRaw, setOverrideValueRaw] = useState(
    toRawString(initialOverrideValue),
  );
  const [note, setNote] = useState(initialNote);
  const [autoRerun, setAutoRerun] = useState(true);

  const isAiSuggested = initialOrigin === 'from_ai_suggestion';

  // The modal is mounted persistently (hidden) by CalculationReviewPanel, so
  // the initial* props can change AFTER mount — e.g. when an AI deep-link
  // seeds a suggestion right before opening. Re-seed the form each time the
  // modal transitions to open so the latest prefills take effect rather than
  // the stale values captured at first mount.
  useEffect(() => {
    if (isOpen) {
      setFieldId(initialFieldId);
      setOriginalValueRaw(toRawString(initialOriginalValue));
      setOverrideValueRaw(toRawString(initialOverrideValue));
      setNote(initialNote);
      setAutoRerun(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    initialFieldId,
    initialOriginalValue,
    initialOverrideValue,
    initialNote,
  ]);

  const engineName = ENGINE_NAMES[engineId];

  const fieldIdTrimmed = trimOrEmpty(fieldId);
  const noteTrimmed = trimOrEmpty(note);
  const noteTooShort = noteTrimmed.length > 0 && noteTrimmed.length < MIN_NOTE_LENGTH;
  const canSubmit =
    fieldIdTrimmed.length > 0 &&
    noteTrimmed.length >= MIN_NOTE_LENGTH &&
    !isSubmitting;

  const reset = () => {
    setFieldId(initialFieldId);
    setOriginalValueRaw(toRawString(initialOriginalValue));
    setOverrideValueRaw(toRawString(initialOverrideValue));
    setNote(initialNote);
    setAutoRerun(true);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      field_id: fieldIdTrimmed,
      override_value: parseValue(overrideValueRaw),
      original_value: parseValue(originalValueRaw),
      note: noteTrimmed,
      auto_trigger_rerun: autoRerun,
      origin: initialOrigin,
      ...(isAiSuggested && initialPromptedByAiAuditId
        ? { prompted_by_ai_audit_id: initialPromptedByAiAuditId }
        : {}),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Add override — ${engineName}`}
    >
      <div className="space-y-4" data-testid="override-form-modal">
        {isAiSuggested && (
          <div
            className="flex items-start gap-2 rounded-md px-3 py-2"
            style={{ border: '1px solid var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}
            data-testid="override-form-ai-banner"
          >
            <Sparkles
              className="mt-0.5 h-4 w-4 flex-shrink-0"
              style={{ color: 'var(--color-needs-human-text)' }}
              aria-hidden="true"
            />
            <div className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
              <p>
                <span className="font-semibold">Proposed by the Forensic Assistant.</span>{' '}
                Review the suggested value, then write your own ≥{MIN_NOTE_LENGTH}-character
                attestation note below — the proposal does not auto-attest, and the
                note must be your own words.
              </p>
              {aiRationale && (
                <p className="mt-1 pt-1 italic" style={{ borderTop: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
                  Assistant's reasoning: {aiRationale}
                </p>
              )}
            </div>
          </div>
        )}
        <div>
          <label
            htmlFor="override-field-id"
            className="block text-sm font-medium"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Field ID <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
          </label>
          <input
            id="override-field-id"
            type="text"
            value={fieldId}
            onChange={(e) => setFieldId(e.target.value)}
            disabled={isSubmitting}
            className="mt-1 block w-full rounded-md shadow-sm sm:text-sm"
            style={{ border: '1px solid var(--color-border)' }}
            placeholder="e.g. policy_deductible_2023"
            data-testid="override-form-field-id"
            aria-required="true"
          />
          <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
            The exact field key the engine output uses. Find it on the
            engine detail card.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="override-original-value"
              className="block text-sm font-medium"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Original value
            </label>
            <input
              id="override-original-value"
              type="text"
              value={originalValueRaw}
              onChange={(e) => setOriginalValueRaw(e.target.value)}
              disabled={isSubmitting}
              className="mt-1 block w-full rounded-md shadow-sm sm:text-sm"
              style={{ border: '1px solid var(--color-border)' }}
              placeholder="(blank for null)"
              data-testid="override-form-original-value"
            />
          </div>
          <div>
            <label
              htmlFor="override-override-value"
              className="block text-sm font-medium"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Override value
            </label>
            <input
              id="override-override-value"
              type="text"
              value={overrideValueRaw}
              onChange={(e) => setOverrideValueRaw(e.target.value)}
              disabled={isSubmitting}
              className="mt-1 block w-full rounded-md shadow-sm sm:text-sm"
              style={{ border: '1px solid var(--color-border)' }}
              placeholder="(blank for null)"
              data-testid="override-form-override-value"
            />
          </div>
        </div>

        {/* Reviewer note — wrapped per Phase 10 Task 10.5 */}
        <CpaWorkpaperTextarea
          id="override-note"
          label={
            <>
              Reviewer note <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
            </>
          }
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={isSubmitting}
          rows={3}
          className="mt-1 block w-full rounded-md shadow-sm sm:text-sm"
          style={{ border: '1px solid var(--color-border)' }}
          placeholder="Why this override (minimum 10 characters)"
          data-testid="override-form-note"
          aria-required="true"
          aria-describedby="override-note-help"
          helperText={
            <span
              id="override-note-help"
              style={{ color: noteTooShort ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)' }}
            >
              {noteTooShort
                ? `Reviewer note must be at least ${MIN_NOTE_LENGTH} characters (currently ${noteTrimmed.length}).`
                : `Persisted to the audit chain. Minimum ${MIN_NOTE_LENGTH} characters.`}
            </span>
          }
        />

        <div className="flex items-start gap-2">
          <input
            id="override-auto-rerun"
            type="checkbox"
            checked={autoRerun}
            onChange={(e) => setAutoRerun(e.target.checked)}
            disabled={isSubmitting}
            className="mt-0.5 rounded"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-brand)' }}
            data-testid="override-form-auto-rerun"
          />
          <label htmlFor="override-auto-rerun" className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            <span className="font-medium">Auto-trigger downstream rerun</span>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              Cascade invalidation flips downstream engines to{' '}
              <code className="px-1 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>stale_after_rerun</code>{' '}
              and queues a Celery rerun task. Uncheck only if you plan to
              apply additional overrides before reviewing downstream.
            </p>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="override-form-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="override-form-submit"
          >
            {isSubmitting ? 'Submitting…' : 'Add override'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
